import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardHeader, 
  CardContent, 
  Button, 
  TextField, 
  Box, 
  CircularProgress, 
  Typography, 
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Divider
} from '@mui/material';
import { 
  Add as AddIcon, 
  Search as SearchIcon, 
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Category as CategoryIcon,
  Inventory as InventoryIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import { productHierarchyService } from '../../services/productHierarchy.service';

// Custom TreeNode component
const TreeNode = ({ node, level = 0, onSelect, expanded, onToggle }) => {
  const nodeId = String(node.node_id);
  const isExpanded = expanded.includes(nodeId);
  const hasChildren = node.children && node.children.length > 0;
  
  // Choose icon based on node level
  const getIcon = () => {
    switch (node.level) {
      case 'department':
        return <StoreIcon />;
      case 'category':
        return <CategoryIcon />;
      case 'subcategory':
        return <InventoryIcon />;
      default:
        return <CategoryIcon />;
    }
  };
  
  return (
    <>
      <ListItem 
        button 
        onClick={() => onSelect(node)}
        sx={{ 
          pl: level * 2 + 2,
          bgcolor: isExpanded ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.08)' }
        }}
      >
        <ListItemIcon onClick={(e) => {
          e.stopPropagation();
          if (hasChildren) {
            onToggle(nodeId);
          }
        }}>
          {hasChildren ? (
            isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />
          ) : (
            getIcon()
          )}
        </ListItemIcon>
        <ListItemText 
          primary={node.name || 'Unnamed Node'} 
          secondary={
            <Typography variant="body2" component="span">
              {node.level}
              {node.sku_count > 0 && ` • ${node.sku_count} SKUs`}
              {node.avg_unit_cost && ` • Avg Cost: $${node.avg_unit_cost.toFixed(2)}`}
            </Typography>
          }
        />
      </ListItem>
      
      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {node.children.map(child => (
            <TreeNode 
              key={child.node_id} 
              node={child} 
              level={level + 1}
              onSelect={onSelect}
              expanded={expanded}
              onToggle={onToggle}
            />
          ))}
        </Collapse>
      )}
    </>
  );
};

const ProductHierarchyTree = ({ onNodeSelect, onCreateNode }) => {
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [expanded, setExpanded] = useState([]);
  
  useEffect(() => {
    loadHierarchyData();
  }, []);
  
  const loadHierarchyData = async () => {
    setLoading(true);
    try {
      const response = await productHierarchyService.getCompleteHierarchy();
      console.log('API Response:', response); // Debug log
      
      // Process the response based on its structure
      let processedData = [];
      
      if (response && response.status === 'success' && response.data) {
        // New API format with status and data fields
        processedData = response.data;
      } else if (Array.isArray(response)) {
        // Old API format (direct array)
        processedData = response;
      } else if (response && Array.isArray(response.data)) {
        // Alternative format with just data field
        processedData = response.data;
      } else {
        console.error('Unexpected hierarchy data format', response);
        processedData = [];
      }
      
      // Check if data is a nested array and flatten it if needed
      if (Array.isArray(processedData) && processedData.length > 0 && Array.isArray(processedData[0])) {
        processedData = processedData.flat();
        console.log('Flattened hierarchy data:', processedData);
      }
      
      setTreeData(processedData);
      
      // Auto-expand top level nodes
      if (processedData.length > 0) {
        const topLevelIds = processedData.map(node => String(node.node_id));
        setExpanded(topLevelIds);
      }
    } catch (error) {
      console.error('Failed to load hierarchy data', error);
      setTreeData([]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleNodeSelect = (node) => {
    if (onNodeSelect && node && node.node_id) {
      // Pass the node ID as a string to the parent component
      onNodeSelect(String(node.node_id));
    }
  };
  
  const handleToggle = (nodeId) => {
    setExpanded(prev => {
      if (prev.includes(nodeId)) {
        return prev.filter(id => id !== nodeId);
      } else {
        return [...prev, nodeId];
      }
    });
  };
  
  const handleSearch = () => {
    if (!searchValue.trim()) {
      return;
    }
    
    const searchLower = searchValue.toLowerCase();
    
    // Find all nodes that match the search
    const findMatchingNodes = (nodes, matches = []) => {
      for (const node of nodes) {
        if (node.name && node.name.toLowerCase().includes(searchLower)) {
          matches.push(String(node.node_id));
        }
        if (node.children && node.children.length > 0) {
          findMatchingNodes(node.children, matches);
        }
      }
      return matches;
    };
    
    const matchingNodeIds = findMatchingNodes(treeData);
    
    // Get all parent node IDs to ensure the matching nodes are visible
    const getAllParentIds = (nodes, childId, parentIds = []) => {
      for (const node of nodes) {
        if (node.children && node.children.length > 0) {
          for (const child of node.children) {
            if (String(child.node_id) === childId) {
              parentIds.push(String(node.node_id));
              return parentIds;
            }
          }
          
          for (const child of node.children) {
            const foundParents = getAllParentIds([child], childId, []);
            if (foundParents.length > 0) {
              parentIds.push(String(node.node_id));
              return [...parentIds, ...foundParents];
            }
          }
        }
      }
      return parentIds;
    };
    
    // Collect all parent IDs for each matching node
    let allParentIds = [];
    for (const nodeId of matchingNodeIds) {
      const parentIds = getAllParentIds(treeData, nodeId);
      allParentIds = [...allParentIds, ...parentIds];
    }
    
    // Expand all parent nodes and matching nodes
    setExpanded([...new Set([...allParentIds, ...matchingNodeIds])]);
  };
  
  return (
    <Card>
      <CardHeader 
        title="Product Hierarchy" 
        action={
          <Stack direction="row" spacing={1}>
            <Button 
              startIcon={<RefreshIcon />} 
              onClick={loadHierarchyData}
              disabled={loading}
            >
              Refresh
            </Button>
            {/* Hide the Add Node button for now
            {onCreateNode && 
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<AddIcon />}
                onClick={onCreateNode}
                disabled={true} // Disable the button
              >
                Add Node
              </Button>
            }
            */}
          </Stack>
        }
      />
      <CardContent>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <TextField 
            placeholder="Search hierarchy..." 
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            size="small"
            sx={{ mr: 1, flexGrow: 1 }}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
            }}
          />
          <Button onClick={handleSearch} variant="outlined">Search</Button>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : treeData.length === 0 ? (
          <Typography align="center" sx={{ p: 3 }}>
            No hierarchy data available
          </Typography>
        ) : (
          <List 
            component="div" 
            sx={{ 
              height: 600, 
              overflowY: 'auto', 
              border: '1px solid rgba(0, 0, 0, 0.12)', 
              borderRadius: 1 
            }}
          >
            {treeData.map(node => (
              <React.Fragment key={node.node_id}>
                <TreeNode 
                  node={node} 
                  onSelect={handleNodeSelect}
                  expanded={expanded}
                  onToggle={handleToggle}
                />
                <Divider />
              </React.Fragment>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductHierarchyTree; 